import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
// stores
import { RestaurantStore } from '../../../core/stores/restaurant.store';
import { CartStore } from 'src/app/core/stores/cart.store';
// models
import { Group, Restaurant, RestaurantAddress, Taxes } from '../../../core/models/restaurant.model';
import { QueueInfo } from '../../../core/models/order.model';
// rxjs
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
// services
import { RouteService } from '../../../core/services/route.service';
import { TabOrdersStatusComponent } from '../tab-orders-status/tab-orders-status.component';
import { ModalController, Platform, PopoverController } from '@ionic/angular';
import { PaymentStore } from '../../../core/stores/payment.store';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { BASE_URL_PATH } from 'src/app/core/constants';
import { LocationListComponent } from '../location-list/location-list.component';
import { OrderApiService } from 'src/app/core/services/order.api.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UtilService } from 'src/app/core/services/util.service';
import { Order } from 'src/app/core/models/payment-item.model';
import { Platforms } from 'src/app/core/enums/util.enum';

@Component({
  selector: 'app-header-small',
  templateUrl: './header-small.component.html',
  styleUrls: ['./header-small.component.scss']
})

export class HeaderSmallComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject();
  private readonly pathRestaurantIndex: number = 1;
  private readonly pathRestaurantIdIndex: number = 2;

  public chosenRestaurant: Restaurant;
  public group: Group;
  public numPersonsAhead: number;
  public queueInfo: QueueInfo;
  public currentRestaurantId: string;
  public restaurants: Array<Restaurant> = [];
  public displayName: string;
  public sourceBanner: string;
  public fromPayment: boolean;
  public logo = '';
  public displayAddress: string;
  public locationId: string;
  public taxes: Taxes[];

  @Input() title: string;
  @Input() isScrollStartPosition = true;
  @Input() showLocationModal = false;
  @Input() isSubscriptionPage = false;
  @Input() confirmationPage = false;

  @Output() locationChanged = new EventEmitter<any>();

  @ViewChild('tabsButton', { static: false, read: ElementRef }) tabsButton: ElementRef;

  public static getQueueInfo(restaurant: Restaurant): QueueInfo {
    return {
      placeInLine: restaurant.placeInLine,
      estimatedTimeToBeginPreparation: restaurant.estimatedTimeToBeginPreparation,
      numPersonsAhead: restaurant.numPersonsAhead,
      maxOrdersInQueue: restaurant.maxOrdersInQueue
    };
  }
  public titleCase(str: string) {
    return str.toLowerCase().split(' ').map(function(word) {
      return (word.charAt(0).toUpperCase() + word.slice(1));
    }).join(' ');
  }

  constructor(
    public cartStore: CartStore,
    public paymentStore: PaymentStore,
    private popoverController: PopoverController,
    private restaurantStore: RestaurantStore,
    private router: Router,
    private route: ActivatedRoute,
    private routeService: RouteService,
    private statusBar: StatusBar,
    private modalController: ModalController,
    private orderApiService: OrderApiService,
    private storageService: StorageService,
    public utilService: UtilService,
    public platform: Platform,
  ) {

  }

  ngOnInit() {
    this.statusBar.styleLightContent();
    this.restaurantStore.taxes$.subscribe((taxes) => {
        if (taxes) {
          this.taxes = taxes;
        }
      });
    if (this.fromPayment) {
      this.cartStore.items.length = 0;
    }
    if (this.restaurantStore.isGroup) {
      this.restaurantStore.group$
        .pipe(filter(Boolean))
        .subscribe((group: Group) => {
          this.group = group;
          this.sourceBanner = group.bannerImageUrl;
          this.logo = group.logoImageUrl || '';
          this.displayName = group.displayName;
        });
    } else {
      this.route.params.subscribe(params => {
        this.restaurantStore
          .getRestaurantById(params.locationId)
          .pipe(takeUntil(this.destroyed$))
          .subscribe(restaurant => {
            if (restaurant) {
              this.locationId = params.locationId;
              this.chosenRestaurant = restaurant;
              this.queueInfo = HeaderSmallComponent.getQueueInfo(restaurant);
              this.sourceBanner = restaurant.locationBanner;
              this.logo = restaurant.logoUrl || '';
              this.setFavicon();
              this.displayName = restaurant.name;
              this.displayAddress = restaurant.address.addressLine;
            }
          });
      });

      this.currentRestaurantId = this.getCurrentRestaurant();
    }

    this.restaurantStore.restaurants$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(restaurants => {
        this.restaurants = restaurants;

        if (restaurants.length > 0) {
          this.numPersonsAhead = restaurants[0].numPersonsAhead;
        }
      });


  }

  ngOnDestroy() {
    this.destroyed$.next();
  }

  public setFavicon() {
    let link: HTMLBaseElement = document.querySelector("link[rel~='icon']");
    if (link && this.logo) {
      link.href = this.logo;
    }
  }

  public async showOrdersInTab(ev) {
    const marginBottom = 10;
    const modalTop = this.tabsButton.nativeElement.getBoundingClientRect().bottom + marginBottom;
    const modalRight = window.innerWidth - this.tabsButton.nativeElement.getBoundingClientRect().right;

    document.documentElement.style.setProperty('--orders-popover-desktop-top', `${modalTop}px`);
    document.documentElement.style.setProperty('--orders-popover-desktop-right', `${modalRight}px`);

    const popover = await this.popoverController.create({
      component: TabOrdersStatusComponent,
      componentProps: {},
      cssClass: ['orders-popover'],
      event: ev,
      translucent: true
    });
    return await popover.present();
  }

  public backToShopPage(): void {
    this.clearData();
    this.restaurantStore.backToStorePage();
  }

  public get banner(): string {
    return `url(${this.sourceBanner ? this.sourceBanner : './assets/images/back.jpg'})`;
  }

  private clearData(): void {
    this.cartStore.deliveryResponseData = null;
    this.cartStore.deliveryAt = null;
    this.cartStore.pickupAt = null;
    this.cartStore.isAsap = true;
    this.restaurantStore.isKioskSmsOrder = false;
  }

  private getCurrentRestaurant(): string {
    const paths = this.router.url.split('/');
    return paths[this.pathRestaurantIndex] === 'locations'
      ? paths[this.pathRestaurantIdIndex]
      : 'switch';
  }

  public navigateToHome(): void {
    if (this.utilService.isStoneCreek() && this.platform.is(Platforms.CORDOVA)) {
    this.utilService.navigateToHome();
    }
  }

}
